// Target stance: built from a few seconds of the rider standing still in their
// chosen stance, ideally captured more than once. Targets live on the board
// plane, so they stay valid if the camera is moved and the tape re-marked.

import { dist, median, medianPoint, meanAngle, robustSigma, spread, type Vec2 } from './geometry';
import { observeFrame, type FootObs } from './features';
import { mappingFromStance, toeSideSign, cameraSide, type PlaneMapping, type Stance } from './mapping';
import { LM, lmX, lmY, lmVis, type PoseFrame } from './pose';

export interface FootTarget {
  /** Plane position of the heel–toe midpoint, cm. */
  centre: Vec2;
  /** Heel→toe direction, radians on the plane. */
  angle: number;
  /** Heel–toe distance on the plane, cm. */
  length: number;
  /** Median normalized image positions while holding the stance (overlays). */
  heelImg: Vec2;
  toeImg: Vec2;
}

export interface Reference {
  front: FootTarget;
  rear: FootTarget;
  /** Per-capture foot centres, cm (repeatability display). */
  captures: { front: Vec2; rear: Vec2 }[];
  /** Typical frame-to-frame jitter (σ, cm) of a still foot's centre. */
  noise: number;
  /** RMS spread of foot centres across captures, cm. */
  repeatability: { front: number; rear: number };
  /** Feet-to-shoulders image height (iso units) while in stance. */
  standHeight: number;
}

export type CalibrationTarget =
  | { kind: 'tape'; mapping: PlaneMapping; stripLength: number; gap: number }
  | { kind: 'stance'; stanceWidth: number; aspect: number };

export interface ReferenceResult {
  reference: Reference | null;
  mapping: PlaneMapping | null;
  problems: string[];
  warnings: string[];
}

interface FootSamples {
  heelImg: Vec2[];
  toeImg: Vec2[];
}

/** Per-label image samples from frames where both feet are confidently visible. */
function collect(frames: readonly PoseFrame[], minVis: number) {
  const L: FootSamples = { heelImg: [], toeImg: [] };
  const R: FootSamples = { heelImg: [], toeImg: [] };
  let usable = 0;
  for (const f of frames) {
    const lm = f.lm;
    if (!lm) continue;
    const ok = [LM.leftHeel, LM.leftToe, LM.rightHeel, LM.rightToe].every((i) => lmVis(lm, i) >= minVis);
    if (!ok) continue;
    usable++;
    const p = (i: number) => ({ x: lmX(lm, i), y: lmY(lm, i) });
    L.heelImg.push(p(LM.leftHeel));
    L.toeImg.push(p(LM.leftToe));
    R.heelImg.push(p(LM.rightHeel));
    R.toeImg.push(p(LM.rightToe));
  }
  return { L, R, usable, total: frames.length };
}

const centreImg = (s: FootSamples) => {
  const h = medianPoint(s.heelImg);
  const t = medianPoint(s.toeImg);
  return { x: (h.x + t.x) / 2, y: (h.y + t.y) / 2 };
};

/**
 * Whether a frame shows someone standing on the tape marks in a stance: one
 * foot near each crosswise strip, within the strips' length, and spread like a
 * stance. Keeps a pause beside the board, or with feet together, from being
 * captured as the target stance.
 */
export function onTapeMarks(f: PoseFrame, mapping: PlaneMapping, tape: { stripLength: number; gap: number }, minVisibility = 0.5): boolean {
  const o = observeFrame(f, mapping, { minVisibility });
  if (!o.L || !o.R) return false;
  const [front, rear] = o.L.centre.y >= o.R.centre.y ? [o.L, o.R] : [o.R, o.L];
  const nearStrip = (y: number, at: number) => Math.abs(y - at) <= 25;
  const onStrip = (x: number) => Math.abs(x) <= tape.stripLength / 2 + 15;
  return (
    nearStrip(front.centre.y, tape.gap) &&
    nearStrip(rear.centre.y, 0) &&
    onStrip(front.centre.x) &&
    onStrip(rear.centre.x) &&
    front.centre.y - rear.centre.y >= 0.6 * tape.gap
  );
}

/**
 * Builds the target stance from one or more still captures.
 * `swapFeet` flips which foot is treated as the front foot (tape-free mode,
 * where only the rider's stance setting decides it).
 */
export function buildReference(
  captures: readonly (readonly PoseFrame[])[],
  target: CalibrationTarget,
  stance: Stance,
  opts: { minVisibility?: number; swapFeet?: boolean } = {},
): ReferenceResult {
  const minVis = opts.minVisibility ?? 0.5;
  const problems: string[] = [];
  const warnings: string[] = [];
  const collected = captures.map((c) => collect(c, minVis));
  const good = collected.filter((c) => c.usable >= Math.max(4, 0.6 * c.total));
  if (good.length === 0) {
    return { reference: null, mapping: null, problems: ['Both feet need to be clearly visible while you hold the stance.'], warnings };
  }
  if (good.length < collected.length) warnings.push(`${collected.length - good.length} capture(s) skipped: feet weren’t clearly visible.`);

  // Establish the plane mapping.
  let mapping: PlaneMapping;
  if (target.kind === 'tape') {
    mapping = target.mapping;
  } else {
    const all = (k: 'L' | 'R') => ({
      heelImg: good.flatMap((c) => c[k].heelImg),
      toeImg: good.flatMap((c) => c[k].toeImg),
    });
    let frontLabel: 'L' | 'R' = stance === 'regular' ? 'L' : 'R';
    if (opts.swapFeet) frontLabel = frontLabel === 'L' ? 'R' : 'L';
    const rearLabel = frontLabel === 'L' ? 'R' : 'L';
    mapping = mappingFromStance(centreImg(all(rearLabel)), centreImg(all(frontLabel)), target.stanceWidth, target.aspect);
  }

  // Observe every usable frame on the plane, assigning front/rear by position.
  const front: FootObs[] = [];
  const rear: FootObs[] = [];
  const perCapture: { front: Vec2; rear: Vec2 }[] = [];
  const jitter: number[] = [];
  const heights: number[] = [];
  let labelVotesFrontIsLeft = 0;
  let labelVotes = 0;
  for (const capture of captures) {
    const cf: FootObs[] = [];
    const cr: FootObs[] = [];
    for (const f of capture) {
      const o = observeFrame(f, mapping, { minVisibility: minVis });
      if (!o.L || !o.R) continue;
      const [fr, re] = o.L.centre.y >= o.R.centre.y ? [o.L, o.R] : [o.R, o.L];
      cf.push(fr);
      cr.push(re);
      labelVotes++;
      if (fr.label === 'L') labelVotesFrontIsLeft++;
      if (o.height !== null) heights.push(o.height);
    }
    if (cf.length < 4) continue;
    front.push(...cf);
    rear.push(...cr);
    perCapture.push({ front: medianPoint(cf.map((o) => o.centre)), rear: medianPoint(cr.map((o) => o.centre)) });
    for (const set of [cf, cr]) {
      jitter.push(robustSigma(set.map((o) => o.centre.x)), robustSigma(set.map((o) => o.centre.y)));
    }
  }
  if (front.length < 4) {
    return { reference: null, mapping: null, problems: ['Couldn’t locate both feet on the floor. Check the camera can see your heels and toes.'], warnings };
  }

  const footTarget = (obs: FootObs[]): FootTarget => ({
    centre: medianPoint(obs.map((o) => o.centre)),
    angle: meanAngle(obs.map((o) => o.angle)),
    length: median(obs.map((o) => o.length)),
    heelImg: medianPoint(obs.map((o) => o.heelImg)),
    toeImg: medianPoint(obs.map((o) => o.toeImg)),
  });
  const ref: Reference = {
    front: footTarget(front),
    rear: footTarget(rear),
    captures: perCapture,
    noise: Math.max(0.3, median(jitter)),
    repeatability: {
      front: spread(perCapture.map((c) => c.front)),
      rear: spread(perCapture.map((c) => c.rear)),
    },
    standHeight: heights.length ? median(heights) : NaN,
  };

  // --- Sanity checks -------------------------------------------------------
  const separation = dist(ref.front.centre, ref.rear.centre);
  if (separation < 20) problems.push('Your feet look too close together for a surf stance. Make sure both feet are visible.');
  if (!(ref.standHeight > 0.1)) warnings.push('Couldn’t see your shoulders clearly; pop-up timing may be unavailable.');
  if (ref.noise > 2.5) warnings.push(`Foot tracking is jittery from this view (±${ref.noise.toFixed(1)} cm). Better light or a closer camera will help.`);
  const rep = Math.max(ref.repeatability.front, ref.repeatability.rear);
  if (perCapture.length > 1 && rep > 6) warnings.push(`Your stance varied by about ${Math.round(rep)} cm between captures. That’s fine, but aim for the same marks each time.`);

  if (target.kind === 'tape') {
    const offFront = Math.abs(ref.front.centre.y - target.gap);
    const offRear = Math.abs(ref.rear.centre.y);
    if (offFront > 15 || offRear > 15) warnings.push('Your feet weren’t on the tape strips. The targets follow where you actually stood.');
    if (Math.abs(ref.front.centre.x) > target.stripLength / 2 || Math.abs(ref.rear.centre.x) > target.stripLength / 2) {
      warnings.push('A foot was beyond the end of its strip. Check the strips are the same length and lined up end to end.');
    }
    const s = toeSideSign(stance);
    if (cameraSide(mapping) !== s) {
      warnings.push('The camera seems to be behind your heels. Feet are easier to see from the side you face.');
    }
    if (labelVotes > 10) {
      const frac = labelVotesFrontIsLeft / labelVotes;
      const expectLeftFront = stance === 'regular';
      if ((expectLeftFront && frac < 0.2) || (!expectLeftFront && frac > 0.8)) {
        warnings.push(`Your ${expectLeftFront ? 'right' : 'left'} foot looked like the front foot. Is your stance ${expectLeftFront ? 'goofy' : 'regular'}?`);
      }
    }
  }
  const toe = toeSideSign(stance);
  const pointing = (t: FootTarget) => Math.cos(t.angle) * toe;
  if (pointing(ref.front) < -0.3 && pointing(ref.rear) < -0.3) {
    warnings.push('Your feet appear to point the opposite way to your stance setting. Check regular/goofy.');
  }
  return { reference: problems.length ? null : ref, mapping, problems, warnings };
}
